// Alpine controller for the homepage testimonials "theme-quote-picker" widget.
//
// The widget lets an operator choose which of the site-wide extracted {theme,quote}
// PAIRS to highlight in the testimonials section (theme_quotes field). Each item is
// a {theme,quote} object. The pool carries SEVERAL quotes per theme (every review's
// analyzed quote for that theme), so list keys use the full theme+quote pair (a
// theme alone is NOT unique → duplicate-key x-for crash); the SELECTION is one quote
// per theme (picking one hides the theme's other quotes). MAX 4 selected.
//
// TRANSACTIONAL shell (working copy + pre-edit snapshot + ONE whole-value
// cms-image-changed on confirm) is shared with review-picker and praise-picker via
// createTransactionalPicker(); this file supplies only the theme-quote-specific
// themeLabels/displayTheme + available()/add()/remove(). No cross-list drag.

import { createTransactionalPicker } from "./transactional-picker";
import { parseJsonOr } from "../shared/utils";

interface CMSThemeQuotePickerConfig {
  field: string;
  // Autosave routing key — threaded into the cms-image-changed detail so the
  // cmsEditor controller routes the write to the right section overlay.
  target: string;
  // Raw JSON strings from the widget ctx (parsed here so the templ never has to
  // build nested Alpine literals): pool = {theme,quote}[], selected = {theme,quote}[]
  // (the ordered theme_quotes selection).
  pool: string;
  selected: string;
  max?: number;
}

interface ThemeQuoteItem {
  theme: string;
  quote: string;
  // Operator-UI localized theme label (property.theme.* namespace), set on POOL
  // entries by the Go handler. Absent on the seeded SELECTED items (they reuse the
  // pool-derived lookup by theme key). Undefined → humanized-key fallback.
  label?: string;
}

const MAX = 4;

export function cmsThemeQuotePicker(config: CMSThemeQuotePickerConfig) {
  const pool = parseJsonOr(config.pool, [] as ThemeQuoteItem[]);
  const selected = parseJsonOr(config.selected, [] as ThemeQuoteItem[]);

  // theme key → operator-UI label, derived from the pool entries (the Go handler
  // resolved property.theme.* per the operator's language). Used for display +
  // search in BOTH panels; selected items resolve their label by theme key here.
  const themeLabels: Record<string, string> = {};
  for (const tq of pool) {
    if (tq.label) themeLabels[tq.theme] = tq.label;
  }

  return {
    ...createTransactionalPicker<ThemeQuoteItem>({
      field: config.field,
      target: config.target,
      selected,
      max: config.max ?? MAX,
    }),
    pool,
    themeLabels,
    // Combobox filter: '' = all themes, else a theme key restricting the POOL panel.
    themeFilter: "",

    // Display label for a theme key: the operator-UI localized label
    // (property.theme.*) when known, else the humanized raw key (underscores →
    // spaces) as a fallback. The STORED value always keeps the raw key — this is
    // render-only.
    displayTheme(theme: string): string {
      return this.themeLabels[theme] || theme.replace(/_/g, " ");
    },

    // Distinct themes present in the pool as {key,label}, sorted by label — the
    // options for the theme-filter combobox. Derived from the full pool (not
    // available()) so the chosen theme stays selectable even once all its quotes
    // are picked.
    themes(): { key: string; label: string }[] {
      const seen = new Set<string>();
      const out: { key: string; label: string }[] = [];
      for (const tq of this.pool) {
        if (seen.has(tq.theme)) continue;
        seen.add(tq.theme);
        out.push({ key: tq.theme, label: this.displayTheme(tq.theme) });
      }
      return out.sort((a, b) => a.label.localeCompare(b.label));
    },

    // Pool pairs whose theme is not yet selected, narrowed by the theme-filter
    // combobox (when set) and the search box (case-insensitive substring on the
    // theme key, quote text, or localized label) — the POOL panel (click-to-add).
    available(): ThemeQuoteItem[] {
      const q = this.search.trim().toLowerCase();
      const tf = this.themeFilter;
      const selectedThemes = new Set(this.selected.map((tq) => tq.theme));
      return this.pool.filter((tq) => {
        if (selectedThemes.has(tq.theme)) return false;
        if (tf && tq.theme !== tf) return false;
        if (!q) return true;
        return (
          tq.theme.toLowerCase().includes(q) ||
          tq.quote.toLowerCase().includes(q) ||
          this.displayTheme(tq.theme).toLowerCase().includes(q)
        );
      });
    },

    // Add a SPECIFIC {theme,quote} pair (the operator picked this quote for this
    // theme). One quote per theme — no-op if the theme is already selected (the
    // pool may carry several quotes for the same theme; picking one hides the rest
    // via available()'s selectedThemes filter) or if at MAX.
    add(theme: string, quote: string) {
      if (this.selected.some((tq) => tq.theme === theme)) return;
      if (this.selected.length >= this.MAX) return;
      this.selected = [...this.selected, { theme, quote }];
    },

    // Drop a pair from the selection by theme key.
    remove(theme: string) {
      const i = this.selected.findIndex((tq) => tq.theme === theme);
      if (i === -1) return;
      this.selected.splice(i, 1);
    },
  };
}
